import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useEliminarPerfil } from "@/hooks/usePerfiles";
import { clasificarRechazoDeBorrado, type Perfil, type RechazoDeBorrado } from "@/lib/perfiles";

interface EliminarPerfilDialogProps {
  /** Perfil a eliminar; `null` cierra el diálogo. */
  perfil: Perfil | null;
  onCerrar: () => void;
  /** El backend corrigió al cliente: este perfil no se puede borrar. */
  onBloqueado: (id: number) => void;
}

/**
 * Confirmación de borrado de un perfil.
 *
 * El backend rechaza con motivos distintos (ver `clasificarRechazoDeBorrado`)
 * y acá se tratan distinto A PROPÓSITO: "en uso" tiene salida (reasignar y
 * reintentar), "bloqueado" no la tiene, así que se retira la acción en vez de
 * dejar al usuario chocando contra el mismo error.
 */
export function EliminarPerfilDialog({
  perfil,
  onCerrar,
  onBloqueado,
}: EliminarPerfilDialogProps) {
  const [rechazo, setRechazo] = useState<RechazoDeBorrado | null>(null);

  /* Si el diálogo ya no está montado cuando llega el rechazo, la alerta de
     adentro no la va a ver nadie: ahí el toast es la ÚNICA señal posible. Con
     el diálogo abierto, el toast sería ruido duplicado sobre la alerta. */
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const eliminar = useEliminarPerfil({
    onSuccess: () => onCerrar(),
    onError: (error) => {
      const rechazoDelServidor = clasificarRechazoDeBorrado(error);

      if (rechazoDelServidor.motivo === "bloqueado" && perfil) {
        onBloqueado(perfil.id);
      }

      if (montado.current) {
        setRechazo(rechazoDelServidor);
        return;
      }

      toast.error(rechazoDelServidor.mensaje);
    },
  });

  if (!perfil) return null;

  // `usuarios_count` solo viene cuando el backend lo cuenta: si no está, no se
  // afirma nada sobre los asignados en vez de inventar un "ninguno".
  const asignados = perfil.usuarios_count;
  const sinSalida = rechazo?.motivo === "bloqueado";
  // `isPending` NO alcanza para bloquear el cierre: sin red, TanStack deja la
  // mutación PAUSADA con `isPending` en true sin haber enviado nada. Como el
  // diálogo es modal y atrapa el foco, condicionar las tres salidas —clic
  // afuera, Escape y Cancelar— solo a `isPending` dejaba al usuario encerrado
  // en la app hasta recuperar conexión o recargar. Pausada NO es en vuelo: si
  // el pedido nunca salió, cerrar es seguro.
  const enVuelo = eliminar.isPending && !eliminar.isPaused;

  function handleEliminar() {
    if (!perfil) return;
    setRechazo(null);
    eliminar.mutate(perfil.id);
  }

  return (
    <AlertDialog
      open
      /* Con el DELETE en vuelo el diálogo NO se cierra: cerrarlo dejaría al
         usuario sin saber si el perfil se borró o no. */
      onOpenChange={(abierto) => {
        if (!abierto && !enVuelo) onCerrar();
      }}
    >
      <AlertDialogContent
        onEscapeKeyDown={(evento) => {
          if (enVuelo) evento.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>¿Eliminar el perfil «{perfil.nombre}»?</AlertDialogTitle>
          <AlertDialogDescription>
            Esta acción no se puede deshacer.
            {asignados === undefined
              ? ""
              : asignados > 0
                ? ` Hoy ${asignados} usuario(s) lo tienen asignado.`
                : " Hoy ningún usuario lo tiene asignado."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {rechazo && (
          <div
            role="alert"
            className="bg-danger-soft text-danger-soft-foreground rounded-md px-3.5 py-3 text-sm"
          >
            <p className="font-bold">{rechazo.mensaje}</p>
            {rechazo.motivo === "en-uso" && (
              <p className="mt-1">
                Reasigna {rechazo.usuariosCount === 1 ? "ese usuario" : `esos ${rechazo.usuariosCount} usuarios`}{" "}
                a otro perfil desde el mantenedor de usuarios y vuelve a intentarlo.
              </p>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={enVuelo}>
            {sinSalida ? "Entendido" : "Cancelar"}
          </AlertDialogCancel>
          {!sinSalida && (
            <Button
              variant="destructive"
              onClick={handleEliminar}
              disabled={enVuelo}
              className="gap-2"
            >
              {enVuelo && <Spinner />}
              Eliminar perfil
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
